import { isListingBuyNowActive } from '@/helpers/listingBuyNowActive'
import type { CombinedListingSearchResult } from '@/queries/CombinedListingSearchResult'

/**
 * The only field the terms helpers below read. Widened from
 * `CombinedListingSearchResult` so callers holding a raw `SearchResult` (e.g.
 * ListingCard) can share this logic instead of restating it.
 */
type TermsRestricted = Partial<
  Pick<CombinedListingSearchResult, 'termsRestrictions'>
>

/**
 * Determines if a listing has terms restrictions that need to be accepted.
 *
 * @param listing - The listing to check
 * @returns true if the listing has terms restrictions
 */
export function hasTermsRestrictions(listing: TermsRestricted): boolean {
  return (
    listing.termsRestrictions !== undefined &&
    listing.termsRestrictions.length > 0
  )
}

/**
 * Determines if a listing has unaccepted terms restrictions.
 * Checks both if terms exist AND if the user has accepted them.
 * Matches the logic from listing details page and termsRestrictionsHelpers.
 *
 * Warning: never use this in a cell renderer. Those do not apply to export.
 * Use this in the valueGetter to properly hide the value.
 *
 * @param listing - The listing to check
 * @param acceptedPolicies - The user's accepted policies (optional)
 * @returns true if the listing has terms that the user has NOT accepted
 */
export function hasUnacceptedTermsRestrictions(
  listing: TermsRestricted,
  acceptedPolicies?: Record<string, unknown> | null
): boolean {
  // If no terms restrictions, return false
  if (!hasTermsRestrictions(listing)) {
    return false
  }

  // If acceptedPolicies is not provided, assume all terms are unaccepted for safety
  if (!acceptedPolicies) {
    return true
  }

  // Check if user has accepted ANY of the required terms
  // If they have accepted ANY term, they're good (matches existing logic)
  const hasAcceptedAnyTerm =
    listing.termsRestrictions?.some(
      (term: string) => term in acceptedPolicies
    ) ?? false

  // Return true if they have NOT accepted any terms
  return !hasAcceptedAnyTerm
}

/**
 * Determines if a listing is currently in Buy Now mode
 * - has Buy Now enabled and
 * - within Buy Now period OR was sold as Buy Now
 *
 * @param listing - The listing to check
 * @returns true if the listing is currently in Buy Now mode
 */
export function isListingBuyNow(listing: CombinedListingSearchResult): boolean {
  return isListingBuyNowActive({
    buyNow: listing.buyNow,
    buyNowEndTime: listing.buyNowEndTime,
    endTime: listing.endTime,
  })
}

/**
 * The listing fields `getBidIneligibilityReason` reads. Widened the same way
 * as `TermsRestricted` so a non-hook caller (e.g. the bid-sheet validator)
 * can share this with `ActionForm.tsx`'s rendering logic.
 */
type BidRoutable = Partial<
  Pick<
    CombinedListingSearchResult,
    'is3MP' | 'pricingStrategy' | 'listingType' | 'termsRestrictions'
  >
>

/**
 * Names which of the conditions below routes a listing away from Bid Now,
 * instead of collapsing all of them into one boolean — a caller showing the
 * reason to the buyer (the bid-sheet validator) needs to say unaccepted
 * terms, Make an Offer and Contract apart, since each has its own
 * actionable remedy. `noActiveAuction` covers the remaining case: a listing
 * with none of the first three conditions but also no live B-Stock auction
 * to bid against (Enterprise/Amazon-Europe, or a 3MP auction with no id yet).
 */
export type BidIneligibilityReason =
  | 'unacceptedTerms'
  | 'makeAnOffer'
  | 'contract'
  | 'noActiveAuction'

/**
 * Why a listing can't take a bid, as opposed to routing to View Details —
 * the same three listing-only conditions `ActionForm.tsx` checks ahead of
 * its `is3MP && pricingStrategy === 'AUCTION'` branch, plus that branch's
 * own negation. `ActionForm` also routes on `isEnded` (the review screen's
 * caller checks `closed`/`canceled` for that instead) and on buyer
 * eligibility (logged-in-as-the-listing's-own-seller, or no active buyer
 * role) — both need a decoded access token this pure check deliberately
 * leaves out; a caller that has one should still gate on it separately.
 *
 * @param listing - The listing to check
 * @param acceptedPolicies - The user's accepted policies (optional)
 * @returns the reason this listing can't take a bid, or null if it can
 */
export function getBidIneligibilityReason(
  listing: BidRoutable,
  acceptedPolicies?: Record<string, unknown> | null
): BidIneligibilityReason | null {
  if (hasUnacceptedTermsRestrictions(listing, acceptedPolicies)) {
    return 'unacceptedTerms'
  }
  if (listing.pricingStrategy === 'MAKE_AN_OFFER') {
    return 'makeAnOffer'
  }
  if (listing.listingType === 'CONTRACT') {
    return 'contract'
  }
  if (!listing.is3MP || listing.pricingStrategy !== 'AUCTION') {
    return 'noActiveAuction'
  }
  return null
}
